// @flow

import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import {DateTime} from 'luxon';
import {useAccount} from 'docs/data';
import {contextStubFunction} from 'docs/util';
import {getTimeWithLocale} from './getTimeWithLocale';

export type TimeContextType = {
  getCurrentTime: () => DateTime,
  getTime: (input: Date | number | string) => DateTime,
};

const TimeContext = React.createContext<TimeContextType>({
  getCurrentTime: contextStubFunction('Time'),
  getTime: contextStubFunction('Time'),
});

type TimeProviderProps = {|
  children: React.Node,
  timestamp: string,
|};

// While the app is spinning up, the "current time" must match between the server
// (during the initial render) and the client (during hydration). Any components
// which work with time should use the TimeProvider rather than creating JavaScript
// Dates or working directly with Luxon.
// (See also RandomProvider.)
export const TimeProvider = (props: TimeProviderProps) => {
  const [frozen, setFrozen] = useState(true);
  const {account} = useAccount();
  const locale = account.locale.code;

  // Once the TimeProvider is mounted, hydration is complete and we can
  // start returning live times instead of the one provided by the server.
  useEffect(() => setFrozen(false), []);

  const context = {
    getCurrentTime: () => {
      const input = frozen ? props.timestamp : new Date();
      return getTimeWithLocale(input, locale);
    },
    getTime: (input) => {
      return getTimeWithLocale(input, locale);
    },
  };

  return (
    <TimeContext.Provider value={context}>
      {props.children}
    </TimeContext.Provider>
  );
};

export const useTime = () => {
  return useContext(TimeContext);
};
